/**
 * Session Monitoring Service
 * 
 * Monitors user session validity during active operations to detect session kicks.
 * Implements lightweight polling with 5-second intervals during active operations.
 * Detects SESSION_KICKED errors and triggers appropriate user notifications.
 * 
 * Requirements: 10.2, 10.5
 * 
 * @fileoverview Frontend session monitoring service
 * @version 1.0.0
 * @since 2025-12-27
 */

import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { securityAuditLogger } from '../utils/securityAuditLogger';

/**
 * Session monitoring configuration
 */
const SESSION_MONITOR_CONFIG = {
  /** Polling interval in milliseconds (5 seconds) */
  POLL_INTERVAL: 5000,
  
  /** Health check endpoint */
  HEALTH_ENDPOINT: '/api/auth/session-health',
  
  /** Maximum consecutive failures before stopping */
  MAX_FAILURES: 3,
  
  /** Delay before showing notification (ms) */
  NOTIFICATION_DELAY: 500
};

/**
 * Session kick notification callback type
 */
export type SessionKickCallback = (details: {
  message: string;
  timestamp: string;
  reason?: string;
}) => void;

/**
 * Session monitoring state
 */
interface SessionMonitorState {
  isMonitoring: boolean;
  intervalId: number | null;
  consecutiveFailures: number;
  lastCheckTime: string | null;
  onSessionKick: SessionKickCallback | null;
}

/**
 * SessionMonitor class
 * 
 * Manages session monitoring lifecycle and handles session kick detection.
 */
export class SessionMonitor {
  private state: SessionMonitorState = {
    isMonitoring: false,
    intervalId: null,
    consecutiveFailures: 0,
    lastCheckTime: null,
    onSessionKick: null
  };

  /**
   * Start session monitoring
   * 
   * Begins lightweight polling to check session validity.
   * Should be called during active operations (e.g., reading creation).
   * 
   * @param onSessionKick - Callback to invoke when session kick is detected
   */
  public start(onSessionKick?: SessionKickCallback): void {
    // Don't start if already monitoring
    if (this.state.isMonitoring) {
      console.log('[SessionMonitor] Already monitoring, skipping start');
      return;
    }

    // Check if user is authenticated
    const { isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated) {
      console.log('[SessionMonitor] User not authenticated, skipping monitoring');
      return;
    }

    console.log('[SessionMonitor] Starting session monitoring');
    
    // Set callback
    if (onSessionKick) {
      this.state.onSessionKick = onSessionKick;
    }

    // Reset state
    this.state.consecutiveFailures = 0;
    this.state.isMonitoring = true;

    // Start polling
    this.state.intervalId = window.setInterval(
      () => this.checkSession(),
      SESSION_MONITOR_CONFIG.POLL_INTERVAL
    );

    // Log monitoring start
    securityAuditLogger.logSecurityEvent('session_monitoring_started', {
      timestamp: new Date().toISOString(),
      pollInterval: SESSION_MONITOR_CONFIG.POLL_INTERVAL
    });
  }

  /**
   * Stop session monitoring
   * 
   * Stops polling and cleans up resources.
   * Should be called when active operations complete or user logs out.
   */
  public stop(): void {
    if (!this.state.isMonitoring) {
      return;
    }

    console.log('[SessionMonitor] Stopping session monitoring');

    // Clear interval
    if (this.state.intervalId !== null) {
      window.clearInterval(this.state.intervalId);
      this.state.intervalId = null;
    }

    // Reset state
    this.state.isMonitoring = false;
    this.state.consecutiveFailures = 0;
    this.state.lastCheckTime = null;

    // Log monitoring stop
    securityAuditLogger.logSecurityEvent('session_monitoring_stopped', {
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Check if monitoring is active
   * 
   * @returns True if session monitoring is currently active
   */
  public isMonitoring(): boolean {
    return this.state.isMonitoring;
  }

  /**
   * Get last check time
   * 
   * @returns ISO timestamp of last session check, or null if never checked
   */
  public getLastCheckTime(): string | null {
    return this.state.lastCheckTime;
  }

  /**
   * Set session kick callback
   * 
   * @param callback - Function to call when session kick is detected
   */
  public setSessionKickCallback(callback: SessionKickCallback): void {
    this.state.onSessionKick = callback;
  }

  /**
   * Check session validity
   * 
   * Makes a lightweight API call to verify session is still valid.
   * Detects SESSION_KICKED errors and triggers appropriate actions.
   * 
   * @private
   */
  private async checkSession(): Promise<void> {
    try {
      // Update last check time
      this.state.lastCheckTime = new Date().toISOString();

      // Make lightweight health check request
      await apiClient.get(SESSION_MONITOR_CONFIG.HEALTH_ENDPOINT);

      // Reset failure counter on success
      if (this.state.consecutiveFailures > 0) {
        console.log('[SessionMonitor] Session check successful, resetting failure counter');
        this.state.consecutiveFailures = 0;
      }

    } catch (error: any) {
      // Check if this is a SESSION_KICKED error
      if (this.isSessionKickedError(error)) {
        console.warn('[SessionMonitor] SESSION_KICKED detected');
        
        // Log session kick detection
        securityAuditLogger.logSecurityEvent('session_kicked_detected', {
          timestamp: new Date().toISOString(),
          errorMessage: error.response?.data?.message,
          details: error.response?.data?.details
        });

        // Stop monitoring
        this.stop();

        // Trigger callback after delay
        setTimeout(() => {
          this.handleSessionKick(error);
        }, SESSION_MONITOR_CONFIG.NOTIFICATION_DELAY);

        return;
      }

      // Handle other errors (network issues, etc.)
      this.state.consecutiveFailures++;
      
      console.warn(
        `[SessionMonitor] Session check failed (${this.state.consecutiveFailures}/${SESSION_MONITOR_CONFIG.MAX_FAILURES})`,
        error.message
      );

      // Stop monitoring after max failures
      if (this.state.consecutiveFailures >= SESSION_MONITOR_CONFIG.MAX_FAILURES) {
        console.error('[SessionMonitor] Max failures reached, stopping monitoring');
        this.stop();
      }
    }
  }

  /**
   * Check if error is a SESSION_KICKED error
   * 
   * @param error - Error object from API call
   * @returns True if error indicates session was kicked
   * @private
   */
  private isSessionKickedError(error: any): boolean {
    return (
      error.response?.status === 401 &&
      error.response?.data?.error === 'SESSION_KICKED'
    );
  }

  /**
   * Handle session kick detection
   * 
   * Invokes callback, logs out user, and redirects to login.
   * 
   * @param error - Error object containing session kick details
   * @private
   */
  private handleSessionKick(error: any): void {
    const message = error.response?.data?.message || 'Your session has been invalidated';
    const details = error.response?.data?.details;
    const timestamp = new Date().toISOString();

    // Invoke callback if set
    if (this.state.onSessionKick) {
      this.state.onSessionKick({
        message,
        timestamp,
        reason: details
      });
    }

    // Log security event
    securityAuditLogger.logSecurityEvent('session_kicked_handled', {
      timestamp,
      message,
      details
    });

    // Logout user (clears tokens and state)
    const { logout } = useAuthStore.getState();
    logout();

    // Redirect to login after short delay
    setTimeout(() => {
      window.location.href = '/login?reason=session_kicked';
    }, 2000);
  }
}

/**
 * Singleton instance of SessionMonitor
 */
export const sessionMonitor = new SessionMonitor();

/**
 * React hook for session monitoring
 * 
 * Provides easy integration with React components.
 * Automatically cleans up monitoring on unmount.
 * 
 * @example
 * ```tsx
 * const { startMonitoring, stopMonitoring, isMonitoring } = useSessionMonitoring({
 *   onSessionKick: (details) => {
 *     toast.error(details.message);
 *   }
 * });
 * 
 * // Start monitoring during reading creation
 * useEffect(() => {
 *   if (isCreatingReading) {
 *     startMonitoring();
 *   } else {
 *     stopMonitoring();
 *   }
 * }, [isCreatingReading]);
 * ```
 */
export function useSessionMonitoring(options?: {
  onSessionKick?: SessionKickCallback;
  autoStart?: boolean;
}) {
  const { onSessionKick, autoStart = false } = options || {};

  // Set callback if provided
  if (onSessionKick) {
    sessionMonitor.setSessionKickCallback(onSessionKick);
  }

  // Auto-start if requested
  if (autoStart && !sessionMonitor.isMonitoring()) {
    sessionMonitor.start();
  }

  return {
    startMonitoring: (callback?: SessionKickCallback) => {
      sessionMonitor.start(callback);
    },
    stopMonitoring: () => {
      sessionMonitor.stop();
    },
    isMonitoring: sessionMonitor.isMonitoring(),
    lastCheckTime: sessionMonitor.getLastCheckTime()
  };
}
